// src/pages/About/AboutPage.js
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './AboutPage.css';
import { useLanguage } from '../../contexts/LanguageContext';

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
  const { translate, language } = useLanguage();
  const containerRef = useRef(null);
  const contactSectionRef = useRef(null);
  const iframeRef = useRef(null);

  // Send language to iframe when it changes
  useEffect(() => {
    const sendLanguageToIframe = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'languageChange', language },
          '*'
        );
      }
    };

    // Send on iframe load
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', sendLanguageToIframe);
      // Also send immediately in case iframe is already loaded
      sendLanguageToIframe();
    }

    return () => {
      if (iframe) {
        iframe.removeEventListener('load', sendLanguageToIframe);
      }
    };
  }, [language]);

  useEffect(() => {
    // Contact Section Animation
    if (contactSectionRef.current) {
      gsap.fromTo(contactSectionRef.current,
        { y: 100, opacity: 0 },
        {
          y: 0, opacity: 1,
          scrollTrigger: {
            trigger: contactSectionRef.current,
            start: 'top bottom',
            end: 'top 70%',
            scrub: true
          }
        }
      );
    }

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="about-page" ref={containerRef}>
      {/* Iframe Container */}
      <div className="about-iframe-container">
        <iframe
          ref={iframeRef}
          src="/ui_design_v20.html"
          title="About TrendMix"
          className="about-iframe"
        />
      </div>

      {/* Contact Section */}
      <section className="about-contact-section" ref={contactSectionRef}>
        <div className="about-contact-content">
          <h2 className="about-contact-title">{translate('aboutPage.contactUs')}</h2>

          <div className="about-social-icons">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="about-social-icon" aria-label="Instagram">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>

            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="about-social-icon" aria-label="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>

            <a href="https://naver.com" target="_blank" rel="noopener noreferrer" className="about-social-icon" aria-label="Naver">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.273 12.845 7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845z" />
              </svg>
            </a>

            <a href="https://kakao.com" target="_blank" rel="noopener noreferrer" className="about-social-icon" aria-label="Kakao">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3zm5.519 8.625h-.96v-2.792h.96v2.792zm-1.8 0h-.96V8.542h.96v3.083zm-3.12 0h-.96V9.417h-.96V8.542h2.88v.875h-.96v2.208zm-2.76 0H7.92V8.542h.96v2.792h.959v.291z" />
              </svg>
            </a>

            <a href="mailto:contact@trendmix.com" className="about-social-icon" aria-label="Email">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
